/**
 * career-werte-view — the Kompass »Meine Werte« read-only display (route
 * `/compass/werte`), the deferred V5 §8 self-view.
 *
 * A calm, non-editing mirror of what Workshop 1 produced: the committed 1·3·4 value
 * pyramid (the shared value-card faces + done-pyramid layout) and the four confirmed
 * statements (»Mein Warum« / »Meine Positionierung« / »Meine Arbeitsphilosophie« /
 * »Meine Vision«). Editing lives in the workshop track — this is the durable read.
 * Honest empty state: when nothing is committed yet, it points to Workshop ①. Data via
 * getValueGame (pyramid) + getWerteSektion (statements), both auth-only reads.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { valueCardById, type ValueCard } from "@career/definition-v2/value-card-catalog"
import type { PersonalStatement, PersonalStatements, PersonalValueEntry, ValuePyramid } from "@career/definition-v2"
import { RouterToken, I18nToken, apiGet } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"

interface ValueGameView {
  pyramid: ValuePyramid | null
}
interface SektionView {
  values: PersonalValueEntry[]
  statements: PersonalStatements | null
  produced: boolean
}

/** The four statement kinds, always in canonical order. */
const STATEMENT_ORDER: PersonalStatement["kind"][] = ["warum", "positionierung", "philosophie", "vision"]
const STATEMENT_LABEL: Record<PersonalStatement["kind"], CareerKey> = {
  warum: "sektion.statement.warum",
  positionierung: "sektion.statement.positionierung",
  philosophie: "sektion.statement.philosophie",
  vision: "sektion.statement.vision",
}

const TIER_LABEL: Record<string, CareerKey> = {
  Lebenswert: "valuecard.tier.lebenswert",
  Superkräfte: "valuecard.tier.superkraefte",
  Basiswerte: "valuecard.tier.basiswerte",
}
const TIER_ORDER = ["Lebenswert", "Superkräfte", "Basiswerte"] as const

export class WerteViewVm implements ViewModel {
  readonly game: Signal<ValueGameView | null> = signal<ValueGameView | null>(null)
  readonly sektion: Signal<SektionView | null> = signal<SektionView | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(private readonly router: Router, i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.game, this.sektion, this.loaded, this.error]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    // Independent reads: a missing/failed sektion still lets the pyramid render (and
    // vice-versa) — one thin fetch never blanks the whole self-view.
    const [game, sektion] = await Promise.all([
      apiGet<ValueGameView>("/api/workshops/values/cardgame").catch(() => null),
      apiGet<SektionView>("/api/workshops/values/sektion").catch(() => null),
    ])
    this.game.set(game)
    this.sektion.set(sektion)
    this.loaded.set(true)
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerWerteView extends KitElement<WerteViewVm> {
  protected createViewModel(ctx: KitContext): WerteViewVm {
    return new WerteViewVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  /** A single value-card face — colour field + theme-aware illustration + the term.
   *  Mirrors the cardgame's `cardFace`: a missing webp simply doesn't paint. */
  private cardFace(card: ValueCard): TemplateResult {
    const media = `--vg-img-l:url('/values/${card.cardId}.light.webp');--vg-img-d:url('/values/${card.cardId}.dark.webp')`
    return html`<span class="vg-card vg-fam-${card.colour}">
      <span class="vg-card-field" aria-hidden="true"></span>
      <span class="vg-card-illus" style=${media} aria-hidden="true"></span>
      <span class="vg-card-term" lang="de">${card.name}</span>
    </span>`
  }

  private pyramid(vm: WerteViewVm, pyramid: ValuePyramid): TemplateResult {
    const byTier = (tier: string) =>
      pyramid.slots.filter((s) => s.tier === tier).map((s) => valueCardById(s.cardId)).filter((c): c is ValueCard => Boolean(c))
    const tierRow = (tier: string) => {
      const cards = byTier(tier)
      if (cards.length === 0) return nothing
      return html`<div class="vg-tier">
        <span class="vg-tier-label eyebrow" lang="de">${vm.t(TIER_LABEL[tier] ?? "valuecard.tier.basiswerte")}</span>
        <ul class="vg-tier-cards" role="list">
          ${cards.map((c) => html`<li class="vg-tier-card vg-fam-${c.colour}">${this.cardFace(c)}</li>`)}
        </ul>
      </div>`
    }
    return html`<div class="vg-pyramid werte-pyramid">${TIER_ORDER.map((t) => tierRow(t))}</div>`
  }

  private statements(vm: WerteViewVm, statements: PersonalStatements): TemplateResult {
    const byKind = new Map(statements.statements.map((s) => [s.kind, s.text]))
    return html`<div class="werte-stmts">
      ${STATEMENT_ORDER.map((k) => {
        const text = byKind.get(k) ?? ""
        return html`<article class="werte-stmt">
          <h3 class="werte-stmt-label eyebrow" lang="de">${vm.t(STATEMENT_LABEL[k])}</h3>
          ${text.trim() ? html`<p class="werte-stmt-body" lang="de">${text}</p>` : html`<p class="werte-stmt-gap surface-empty" lang="de">${vm.t("werte.view.stmtGap")}</p>`}
        </article>`
      })}
    </div>`
  }

  private empty(vm: WerteViewVm): TemplateResult {
    return html`<div class="werte-empty surface-panel">
      <p class="surface-lead" lang="de">${vm.t("werte.view.empty")}</p>
      <button class="primary" data-act="to-workshop" lang="de">${vm.t("werte.view.toWorkshop")}</button>
    </div>`
  }

  protected render(vm: WerteViewVm): TemplateResult {
    const pyramid = vm.game.value?.pyramid ?? null
    const sektion = vm.sektion.value
    const hasStatements = Boolean(sektion?.produced && sektion.statements)
    const anything = Boolean(pyramid) || hasStatements
    return html`
      <section class="surface werte-view" aria-labelledby="werte-title">
        <p class="eyebrow" lang="de">${vm.t("werte.view.kicker")}</p>
        <h1 id="werte-title" class="surface-title" lang="de">${vm.t("surface.werte.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("werte.view.lead")}</p>
        ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}
        ${!vm.loaded.value
          ? nothing
          : !anything
            ? this.empty(vm)
            : html`
                ${pyramid
                  ? html`<section class="werte-block">
                      <h2 class="surface-section-title" lang="de">${vm.t("werte.view.pyramidTitle")}</h2>
                      ${this.pyramid(vm, pyramid)}
                    </section>`
                  : nothing}
                ${hasStatements && sektion?.statements
                  ? html`<section class="werte-block">
                      <h2 class="surface-section-title" lang="de">${vm.t("werte.view.statementsTitle")}</h2>
                      ${this.statements(vm, sektion.statements)}
                    </section>`
                  : nothing}`}
      </section>
    `
  }

  protected override actions(vm: WerteViewVm): ActionMap {
    return {
      "to-workshop": () => vm.go("/workshops/values"),
    }
  }
}
